import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, effect, inject, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { Subject, takeUntil } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { ModalEvChargerDetailComponent } from '../../../modals/ev-charger/modal-ev-charger-detail/modal-ev-charger-detail.component';
import { ModalEvChargerLicenseEditComponent } from '../../../modals/ev-charger/modal-ev-charger-license-edit/modal-ev-charger-license-edit.component';
import { SendManualPlateCorrectionPayload, TransactionEVDto } from '../../../../../core/models/ev.model';
import { EvChargerTransactionChartItemDto } from '../../../../../core/models/ev/ev-charger-list.model';
import { ApiEvChargerListService } from '../../../../../core/services/api/ev/api-ev-charger-list.service';
import { MapperEvChargerListService } from '../../../../../core/mappers/ev/mapper-ev-charger-list.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';

@Component({
  selector: 'app-ev-charger-list',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalEvChargerDetailComponent, ModalEvChargerLicenseEditComponent],
  templateUrl: './ev-charger-list.component.html',
  styleUrls: ['./ev-charger-list.component.css']
})
export class EvChargerListComponent implements OnInit, OnDestroy {
  readonly siteState = inject(SiteStateService);
  private readonly apiService = inject(ApiEvChargerListService);
  private readonly mapper = inject(MapperEvChargerListService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly destroy$ = new Subject<void>();

  startDate = '';
  endDate = '';
  userId = 0;

  searchText = '';
  searchType: 'licensePlate' | 'siteName' | 'spaceCode' = 'licensePlate';
  isSearchTypeDropdownOpen = false;
  minPrice: number | null = null;
  showNotExitedOnly = false;
  showExitedOnly = false;

  filterStatus: Record<number, boolean> = {
    0: false,
    1: false,
  };

  showTableFilters = false;
  statusOptions = [
    { value: 0, label: 'รอชาร์จ', color: 'text-amber-500' },
    { value: 1, label: 'กำลังชาร์จ', color: 'text-blue-500' },
  ];

  transactions: TransactionEVDto[] = [];
  isLoading = false;
  lastFetchedTime: Date | null = null;

  currentPage = 1;
  pageSize = 20;
  pageSizeOptions: number[] = [10, 20, 50, 100];
  totalItems = 0;
  totalPages = 1;

  dashboardStats = {
    totalTransactions: 0,
    waiting: 0,
    activeCharging: 0,
    completed: 0,
    totalRevenue: 0
  };

  donutStats = {
    totalTransactions: 0,
    waiting: 0,
    activeCharging: 0,
    completed: 0
  };

  get donutTotal(): number {
    const total = this.donutStats.waiting + this.donutStats.activeCharging + this.donutStats.completed;
    return total > 0 ? total : 1;
  }

  get waitingPct(): number {
    return (this.donutStats.waiting / this.donutTotal) * 100;
  }

  get activePct(): number {
    return (this.donutStats.activeCharging / this.donutTotal) * 100;
  }

  get completedPct(): number {
    return (this.donutStats.completed / this.donutTotal) * 100;
  }

  showDashboard = true;
  showMobileFilter = false;
  collapsedFilters: Record<string, boolean> = {};

  enableLiveRefresh = false;
  private liveRefreshInterval: ReturnType<typeof setInterval> | null = null;

  dashboardData: EvChargerTransactionChartItemDto[] = [];
  chartMode: 'daily' | 'monthly' = 'daily';
  dailySubMode: 'default' | 'custom' = 'default';
  customStartDate = '';
  customEndDate = '';
  chartType: 'line' | 'bar' = 'line';
  selectedMonthStart = new Date().toISOString().slice(0, 7);
  selectedMonthEnd = new Date().toISOString().slice(0, 7);
  yAxisLabels: number[] = [];
  currentMonthPoints = '';
  currentMonthAreaPoints = '';
  currentMonthDots: any[] = [];
  chartTotalCount = 0;
  recentActivity: any[] = [];

  isDetailModalOpen = false;
  selectedTransaction: any = null;
  isLicenseEditModalOpen = false;
  selectedLicenseEditData: (SendManualPlateCorrectionPayload & { transactionEvId?: number }) | null = null;

  sortColumn = 'enterAt';
  sortDirection: 'asc' | 'desc' = 'desc';

  constructor() {
    const today = new Date().toISOString().slice(0, 10);
    this.startDate = today;
    this.endDate = today;

    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) return;

      untracked(() => {
        this.currentPage = 1;
        this.loadTransactions();
        this.loadChartData();
      });
    });
  }

  ngOnInit(): void {
    const userIdLocal = localStorage.getItem('userId');
    if (userIdLocal) {
      this.userId = Number(userIdLocal);
    } else {
      this.userId = Number(this.cookieService.get('user_id'));
    }

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearLiveRefreshInterval();
  }

  onLiveRefreshToggle(): void {
    if (this.enableLiveRefresh) {
      this.startLiveRefreshInterval();
    } else {
      this.clearLiveRefreshInterval();
    }
  }

  private startLiveRefreshInterval(): void {
    this.clearLiveRefreshInterval();
    this.liveRefreshInterval = setInterval(() => {
      this.loadTransactions(true);
      this.loadChartData(true);
    }, 60000);
  }

  private clearLiveRefreshInterval(): void {
    if (this.liveRefreshInterval) {
      clearInterval(this.liveRefreshInterval);
      this.liveRefreshInterval = null;
    }
  }

  get pagesArray(): number[] {
    const total = this.totalPages;
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage - 2);
    let endPage = Math.min(total, startPage + maxVisibleButtons - 1);

    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }

    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get activeFilterCount(): number {
    let count = 0;
    if (Object.values(this.filterStatus).some(val => val)) count++;
    if (this.showNotExitedOnly) count++;
    if (this.showExitedOnly) count++;
    if (this.searchText) count++;
    if (this.minPrice !== null) count++;
    return count;
  }

  toggleDashboard(): void {
    this.showDashboard = !this.showDashboard;
  }

  toggleMobileFilter(): void {
    this.showMobileFilter = !this.showMobileFilter;
  }

  closeMobileFilter(): void {
    this.showMobileFilter = false;
  }

  toggleFilterSection(section: string): void {
    this.collapsedFilters[section] = !this.collapsedFilters[section];
  }

  isFilterCollapsed(section: string): boolean {
    return !!this.collapsedFilters[section];
  }

  clearAllFilters(): void {
    this.filterStatus = { 0: false, 1: false };
    this.showNotExitedOnly = false;
    this.showExitedOnly = false;
    this.searchText = '';
    this.minPrice = null;
    this.currentPage = 1;
    this.loadTransactions();
  }

  loadTransactions(isSilent = false): void {
    const siteId = this.siteState.selectedSite()?.id;
    if (!siteId || !this.userId) return;

    if (!isSilent) this.isLoading = true;

    const searchField = this.searchText.trim()
      ? this.mapper.mapSearchField(this.searchType)
      : undefined;

    this.apiService.getEvChargerTransactionForTable({
      siteId,
      userId: this.userId,
      startDate: this.startDate,
      endDate: this.endDate,
      page: this.currentPage,
      pageSize: this.pageSize,
      searchField,
      searchText: this.searchText.trim() || undefined,
      sortColumn: this.mapper.mapSortColumn(this.sortColumn),
      sortDirection: this.sortDirection,
      statusCodes: this.mapper.mapStatusCodes(this.filterStatus),
      exitFilter: this.mapper.mapExitFilter(this.showNotExitedOnly, this.showExitedOnly),
      minPrice: this.minPrice ?? undefined,
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapTableFromDto(res);
        this.transactions = mapped.items;
        this.totalItems = mapped.total;
        this.totalPages = mapped.totalPages || 1;

        if (mapped.summary) {
          this.dashboardStats = mapped.summary.dashboard;
          this.donutStats = mapped.summary.donut;
        }

        this.lastFetchedTime = new Date();
        if (!isSilent) this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        if (!isSilent) {
          this.toast.error('ไม่สามารถโหลดข้อมูล EV Transaction ได้');
          this.isLoading = false;
        }
      },
    });
  }

  loadChartData(isSilent = false): void {
    const siteId = this.siteState.selectedSite()?.id;
    if (!siteId || !this.userId) return;

    let start = this.startDate;
    let end = this.endDate;

    if (this.chartMode === 'daily') {
      if (this.dailySubMode === 'custom' && this.customStartDate && this.customEndDate) {
        start = this.customStartDate;
        end = this.customEndDate;
      }
    } else if (this.chartMode === 'monthly') {
      const [startYear, startMonth] = this.selectedMonthStart.split('-').map(Number);
      const [endYear, endMonth] = this.selectedMonthEnd.split('-').map(Number);
      if (startYear && startMonth && endYear && endMonth) {
        const startDateObj = new Date(startYear, startMonth - 1, 1);
        const endDateObj = new Date(endYear, endMonth, 0);
        start = this.formatDate(startDateObj);
        end = this.formatDate(endDateObj);
      }
    }

    this.apiService.getEvChargerTransactionChart({
      siteId,
      userId: this.userId,
      startDate: start,
      endDate: end,
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.dashboardData = this.mapper.mapChartItems(res);
        this.processDashboardData();
      },
      error: (err) => {
        if (!isSilent) console.error('Error fetching chart data:', err);
      },
    });
  }

  toggleStatus(status: number): void {
    if (this.filterStatus[status] === undefined) this.filterStatus[status] = true;
    this.filterStatus[status] = !this.filterStatus[status];
    this.filterData();
  }

  isStatusChecked(status: number): boolean {
    return this.filterStatus[status] !== false;
  }

  sortData(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'desc';
    }
    this.currentPage = 1;
    this.loadTransactions();
  }

  filterData(): void {
    this.currentPage = 1;
    this.loadTransactions();
  }

  toggleSearchTypeDropdown(): void {
    this.isSearchTypeDropdownOpen = !this.isSearchTypeDropdownOpen;
  }

  selectSearchType(type: 'licensePlate' | 'siteName' | 'spaceCode'): void {
    this.searchType = type;
    this.isSearchTypeDropdownOpen = false;
    this.filterData();
  }

  get currentSearchTypeLabel(): string {
    switch (this.searchType) {
      case 'licensePlate': return 'ทะเบียนรถ';
      case 'siteName': return 'ชื่อสถานที่';
      case 'spaceCode': return 'รหัสช่องจอด';
      default: return 'ค้นหา';
    }
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadTransactions();
  }

  onPageSizeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.pageSize = Number(target.value);
    this.currentPage = 1;
    this.loadTransactions();
  }

  onDateChange(): void {
    this.currentPage = 1;
    this.loadTransactions();
    if (this.chartMode === 'daily' && this.dailySubMode === 'default') {
      this.loadChartData();
    }
  }

  onRefresh(): void {
    this.searchText = '';
    this.minPrice = null;
    this.filterStatus = { 0: false, 1: false };
    this.showNotExitedOnly = false;
    this.showExitedOnly = false;

    const today = new Date().toISOString().slice(0, 10);
    this.startDate = today;
    this.endDate = today;
    this.currentPage = 1;

    this.loadTransactions();
    this.loadChartData();
  }

  formatDateDisplay(dateStr: string): string {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    const hour = d.getHours().toString().padStart(2, '0');
    const min = d.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hour}:${min}`;
  }

  getStatusLabel(status: number, exitAt: Date | null): string {
    switch (status) {
      case 0: return exitAt === null ? 'รอชาร์จ' : 'เสร็จสิ้น';
      case 1: return 'กำลังชาร์จ';
      default: return `สถานะ ${status}`;
    }
  }

  getStatusClass(status: number, exitAt: Date | null): string {
    switch (status) {
      case 0: return exitAt === null ? 'bg-amber-50 text-amber-700 ring-amber-200' : 'bg-emerald-50 text-emerald-700 ring-emerald-200';
      case 1: return 'bg-blue-50 text-blue-700 ring-blue-200';
      default: return 'bg-slate-50 text-slate-700 ring-slate-200';
    }
  }

  formatDate(date: Date): string {
    return date.toISOString().slice(0, 10);
  }

  getTimeAgo(dateInput: string | Date | null | undefined): string {
    if (!dateInput) return '-';
    const date = new Date(dateInput);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + ' ปีที่แล้ว';
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + ' เดือนที่แล้ว';
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + ' วันที่แล้ว';
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + ' ชั่วโมงที่แล้ว';
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + ' นาทีที่แล้ว';
    return 'เพิ่งสร้าง';
  }

  processDashboardData(): void {
    let xLabels: Date[] = [];
    let dataPoints: number[] = [];
    let maxVal = 5;

    if (this.chartMode === 'daily') {
      let start: Date;
      let end: Date;

      if (this.dailySubMode === 'custom' && this.customStartDate && this.customEndDate) {
        start = new Date(this.customStartDate);
        end = new Date(this.customEndDate);
      } else {
        start = new Date(this.startDate);
        end = new Date(this.endDate);
      }

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        xLabels.push(new Date(d));
        dataPoints.push(0);
      }

      this.dashboardData.forEach(m => {
        if (!m.enterAt) return;
        const d = new Date(m.enterAt);
        const dDateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());

        if (dDateOnly >= startDateOnly && dDateOnly <= endDateOnly) {
          const idx = xLabels.findIndex(x => x.toDateString() === dDateOnly.toDateString());
          if (idx !== -1) dataPoints[idx]++;
        }
      });
    } else {
      const [startYear, startMonth] = this.selectedMonthStart.split('-').map(Number);
      const [endYear, endMonth] = this.selectedMonthEnd.split('-').map(Number);

      const start = new Date(startYear, startMonth - 1, 1);
      const end = new Date(endYear, endMonth, 0);

      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays <= 60) {
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          xLabels.push(new Date(d));
          dataPoints.push(0);
        }
        this.dashboardData.forEach(m => {
          if (!m.enterAt) return;
          const d = new Date(m.enterAt);
          if (d >= start && d <= end) {
            const idx = xLabels.findIndex(x => x.toDateString() === d.toDateString());
            if (idx !== -1) dataPoints[idx]++;
          }
        });
      } else {
        let temp = new Date(startYear, startMonth - 1, 1);
        while (temp <= end) {
          const year = temp.getFullYear();
          const month = temp.getMonth();
          const chunks = [1, 6, 11, 16, 21, 26];

          chunks.forEach(day => {
            const chunkDate = new Date(year, month, day);
            if (chunkDate <= end && chunkDate >= start) {
              xLabels.push(chunkDate);
              dataPoints.push(0);
            }
          });
          temp.setMonth(temp.getMonth() + 1);
        }

        this.dashboardData.forEach(m => {
          if (!m.enterAt) return;
          const d = new Date(m.enterAt);
          if (d < start || d > end) return;

          let chunkStart = 1;
          const date = d.getDate();
          if (date >= 26) chunkStart = 26;
          else if (date >= 21) chunkStart = 21;
          else if (date >= 16) chunkStart = 16;
          else if (date >= 11) chunkStart = 11;
          else if (date >= 6) chunkStart = 6;

          const idx = xLabels.findIndex(x =>
            x.getFullYear() === d.getFullYear() &&
            x.getMonth() === d.getMonth() &&
            x.getDate() === chunkStart
          );
          if (idx !== -1) dataPoints[idx]++;
        });
      }
    }

    this.chartTotalCount = dataPoints.reduce((a, b) => a + b, 0);

    const rawMax = Math.max(...dataPoints, 5);
    maxVal = Math.ceil(rawMax / 4) * 4;
    this.yAxisLabels = [0, maxVal * 0.25, maxVal * 0.5, maxVal * 0.75, maxVal];

    const getCoordinate = (val: number, index: number, total: number) => {
      if (total <= 1) return { x: 50, y: 100 - ((val / maxVal) * 100) };
      const x = 2 + (index / (total - 1)) * 96;
      const y = 100 - ((val / maxVal) * 100);
      return { x, y };
    };

    const getThaiMonth = (monthIndex: number) => {
      const months = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
      return months[monthIndex];
    };

    const pointsObj = dataPoints.map((val, i) => getCoordinate(val, i, dataPoints.length));
    this.currentMonthPoints = pointsObj.map(p => `${p.x},${p.y}`).join(' ');

    const [sY, sM] = this.selectedMonthStart.split('-').map(Number);
    const [eY, eM] = this.selectedMonthEnd.split('-').map(Number);
    const mStart = new Date(sY, sM - 1, 1);
    const mEnd = new Date(eY, eM, 0);
    const mDiff = Math.ceil(Math.abs(mEnd.getTime() - mStart.getTime()) / (1000 * 60 * 60 * 24));
    const isDailyGranularity = this.chartMode === 'daily' || (this.chartMode === 'monthly' && mDiff <= 60);

    this.currentMonthDots = pointsObj.map((p, i) => {
      const date = xLabels[i];
      let label = '';
      let subLabel = '';

      if (isDailyGranularity) {
        label = `${date.getDate()}`;
        if (date.getDate() === 1 || i === 0) {
          subLabel = `${getThaiMonth(date.getMonth())}`;
          label = `${date.getDate()}`;
        }
      } else {
        const day = date.getDate();
        if (day === 26) {
          const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
          label = `26-${lastDay}`;
        } else {
          label = `${day}-${day + 4}`;
        }
        if (day === 1) {
          subLabel = `${getThaiMonth(date.getMonth())} ${date.getFullYear()}`;
        }
      }

      return {
        x: p.x,
        y: p.y,
        label,
        subLabel,
        value: dataPoints[i],
        color: this.getChartColor(i)
      };
    });

    if (pointsObj.length > 0) {
      const firstPoint = pointsObj[0];
      const lastPoint = pointsObj[pointsObj.length - 1];
      this.currentMonthAreaPoints = `
          ${firstPoint.x},100
          ${this.currentMonthPoints}
          ${lastPoint.x},100
        `;
    } else {
      this.currentMonthAreaPoints = '';
    }

    const sortedByDate = [...this.dashboardData].sort((a, b) => {
      const dateA = a.enterAt ? new Date(a.enterAt).getTime() : 0;
      const dateB = b.enterAt ? new Date(b.enterAt).getTime() : 0;
      return dateB - dateA;
    });

    this.recentActivity = sortedByDate.slice(0, 5).map(m => ({
      initial: m.licensePlate ? m.licensePlate.charAt(0).toUpperCase() : '?',
      name: m.licensePlate,
      timeAgo: this.getTimeAgo(m.enterAt),
      info: '฿ ' + (m.price || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      color: this.getRandomColor(m.id)
    }));
  }

  getRandomColor(id: number): string {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500'];
    return colors[id % colors.length];
  }

  getChartColor(index: number): string {
    const colors = ['#fbbf24', '#f59e0b', '#34d399', '#60a5fa', '#f87171', '#818cf8', '#a78bfa', '#f472b6'];
    return colors[index % colors.length];
  }

  setChartMode(mode: 'daily' | 'monthly'): void {
    this.chartMode = mode;
    if (mode === 'monthly') {
      const today = new Date();
      const currentMonthStub = today.toISOString().slice(0, 7);
      const prevMonthDate = new Date(today.getFullYear(), today.getMonth(), 1);
      const prevMonthStub = prevMonthDate.toISOString().slice(0, 7);
      this.selectedMonthStart = prevMonthStub;
      this.selectedMonthEnd = currentMonthStub;
      this.onChartMonthChange();
      return;
    }
    this.loadChartData();
  }

  setChartType(type: 'line' | 'bar'): void {
    this.chartType = type;
  }

  onChartMonthChange(): void {
    if (this.chartMode !== 'monthly') return;
    this.loadChartData();
  }

  setDailySubMode(mode: 'default' | 'custom'): void {
    this.dailySubMode = mode;
    if (mode === 'custom') {
      if (!this.customStartDate) this.customStartDate = this.startDate;
      if (!this.customEndDate) {
        const s = new Date(this.customStartDate);
        const e = new Date(this.customStartDate);
        e.setDate(s.getDate() + 6);
        this.customEndDate = this.formatDate(e);
      }
      this.onCustomDateChange();
    } else {
      this.loadChartData();
    }
  }

  get maxCustomDate(): string {
    if (!this.customStartDate) return '';
    const d = new Date(this.customStartDate);
    d.setDate(d.getDate() + 10);
    return this.formatDate(d);
  }

  resetCustomDate(): void {
    const e = new Date();
    const s = new Date();
    s.setDate(e.getDate() - 9);
    this.customEndDate = this.formatDate(e);
    this.customStartDate = this.formatDate(s);
    this.loadChartData();
  }

  onCustomDateChange(): void {
    if (!this.customStartDate || !this.customEndDate) return;
    const s = new Date(this.customStartDate);
    const e = new Date(this.customEndDate);
    if (e < s) this.customEndDate = this.customStartDate;
    const sTime = new Date(this.customStartDate).getTime();
    const eTime = new Date(this.customEndDate).getTime();
    const diffDays = Math.ceil((eTime - sTime) / (1000 * 60 * 60 * 24));
    if (diffDays > 10) {
      this.toast.warn('แสดงผลได้สูงสุด 10 วัน');
      const newEnd = new Date(s);
      newEnd.setDate(s.getDate() + 9);
      this.customEndDate = this.formatDate(newEnd);
    }
    this.loadChartData();
  }

  calculateTime(totalTime: number): string {
    const hours = Math.floor(totalTime / 60);
    const minutes = totalTime % 60;
    return `${hours} ชม. ${minutes} นาที`;
  }

  openDetailModal(item: TransactionEVDto): void {
    this.selectedTransaction = {
      id: item.id,
      historyUuid: item.historyUuid,
      licensePlate: item.licensePlate,
      siteEv: item.siteEv,
      spaceCode: item.spaceCode
    };
    this.isDetailModalOpen = true;
  }

  closeDetailModal(): void {
    this.isDetailModalOpen = false;
    this.selectedTransaction = null;
  }

  openLicenseEditModal(item: TransactionEVDto): void {
    this.selectedLicenseEditData = {
      historyUid: item.historyUuid,
      licensePlate: item.licensePlate,
      siteEvName: item.siteEv || item.siteName,
      spaceName: item.spaceCode,
      userId: this.userId,
      transactionEvId: item.id,
    };
    this.isLicenseEditModalOpen = true;
  }

  closeLicenseEditModal(): void {
    this.isLicenseEditModalOpen = false;
    this.selectedLicenseEditData = null;
  }

  onLicenseSaved(_newLicensePlate: string): void {
    if (this.selectedLicenseEditData) {
      this.loadTransactions(true);
      this.toast.success('บันทึกทะเบียนรถเรียบร้อย');
    }
  }
}
